import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import type { TournamentFormState } from "@/api/tournaments";
import type { TournamentValidationErrors } from "@/hooks/useTournamentValidation";

interface TournamentReviewProps {
  tournament: TournamentFormState;
  validationErrors: TournamentValidationErrors;
  onStepChange: (step: number) => void;
}

export function TournamentReview({
  tournament,
  validationErrors,
  onStepChange,
}: TournamentReviewProps) {
  const { basicInfo, audience, content, settings } = tournament;

  const audienceSummary = audience.isGlobal
    ? "This tournament is available to everyone"
    : `${audience.users.length} ${audience.users.length === 1 ? "user" : "users"} selected`;

  const hasSettingsError =
    validationErrors.settings.startDate.hasError ||
    validationErrors.settings.endDate.hasError ||
    validationErrors.settings.contactPersons.hasError;

  return (
    <div className="grid 2xl:grid-cols-2 gap-4">
      {/* Name & Description */}
      <Card className={`h-70 overflow-y-auto ${themeClasses.bgElevation2}`}>
        <div className="flex justify-between items-center">
          <div className="flex gap-1 items-center mb-2">
            <h4 className="font-semibold text-lg">1. Name & Description</h4>
            {validationErrors.nameDescription.hasError && (
              <span className="ml-2">
                <i
                  className={`pi pi-info-circle ${themeClasses.iconError}`}
                  style={{ fontSize: "1rem" }}
                  id="name-description-validation-error"
                />
                <Tooltip target="#name-description-validation-error" position="right">
                  {validationErrors.nameDescription.message}
                </Tooltip>
              </span>
            )}
          </div>
          <Button label="Edit" onClick={() => onStepChange(0)} link />
        </div>
        <div>
          {validationErrors.nameDescription.hasError ? (
            <div>Please provide a name and description for your tournament.</div>
          ) : (
            <div className="ml-6">
              <div className="mt-2">Tournament Name: {basicInfo.name}</div>
              <div className="mt-2">Tournament Description: {basicInfo.description}</div>
            </div>
          )}
        </div>
      </Card>

      {/* Audience */}
      <Card className={`h-70 overflow-y-auto ${themeClasses.bgElevation2}`}>
        <div className="flex justify-between items-center">
          <div className="flex gap-1 items-center mb-2">
            <h4 className="font-semibold text-lg">2. Audience</h4>
            {validationErrors.audience.hasError && (
              <span className="ml-2">
                <i
                  className={`pi pi-info-circle ${themeClasses.iconError}`}
                  style={{ fontSize: "1rem" }}
                  id="audience-validation-error"
                />
                <Tooltip target="#audience-validation-error" position="right">
                  {validationErrors.audience.message}
                </Tooltip>
              </span>
            )}
          </div>
          <Button label="Edit" onClick={() => onStepChange(1)} link />
        </div>
        <div>
          {validationErrors.audience.hasError ? (
            <div>Please add at least one user to your tournament audience.</div>
          ) : (
            <div className="ml-6">{audienceSummary}</div>
          )}
        </div>
      </Card>

      {/* Content */}
      <Card className={`h-70 overflow-y-auto ${themeClasses.bgElevation2}`}>
        <div className="flex justify-between items-center">
          <div className="flex gap-1 items-center mb-2">
            <h4 className="font-semibold text-lg">3. Content</h4>
            {validationErrors.content.hasError && (
              <span className="ml-2">
                <i
                  className={`pi pi-info-circle ${themeClasses.iconError}`}
                  style={{ fontSize: "1rem" }}
                  id="content-validation-error"
                />
                <Tooltip target="#content-validation-error" position="right">
                  {validationErrors.content.message}
                </Tooltip>
              </span>
            )}
          </div>
          <Button label="Edit" onClick={() => onStepChange(2)} link />
        </div>
        <div>
          {validationErrors.content.hasError ? (
            <div>Please select at least one lesson for your tournament.</div>
          ) : (
            <div className="ml-6">
              {content.selected.map(lesson => (
                <div key={lesson.lessonKey ?? lesson.id}>{lesson.name}</div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Settings */}
      <Card className={`h-70 overflow-y-auto ${themeClasses.bgElevation2}`}>
        <div className="flex justify-between items-center">
          <div className="flex gap-1 items-center mb-2">
            <h4 className="font-semibold text-lg">4. Tournament Settings</h4>
            {hasSettingsError && (
              <span className="ml-2">
                <i
                  className={`pi pi-info-circle ${themeClasses.iconError}`}
                  style={{ fontSize: "1rem" }}
                  id="settings-validation-error"
                />
                <Tooltip target="#settings-validation-error" position="right">
                  <ul className="list-disc pl-4">
                    {validationErrors.settings.startDate.hasError && (
                      <li>{validationErrors.settings.startDate.message}</li>
                    )}
                    {validationErrors.settings.endDate.hasError && (
                      <li>{validationErrors.settings.endDate.message}</li>
                    )}
                    {validationErrors.settings.contactPersons.hasError && (
                      <li>{validationErrors.settings.contactPersons.message}</li>
                    )}
                  </ul>
                </Tooltip>
              </span>
            )}
          </div>
          <Button label="Edit" onClick={() => onStepChange(3)} link />
        </div>
        <div>
          {hasSettingsError ? (
            <div>Please correct the errors in your tournament settings.</div>
          ) : (
            <div className="ml-6">
              <div>
                Start Date:{" "}
                {settings.startDate
                  ? new Date(settings.startDate as string | Date).toLocaleString(undefined, {
                      timeZoneName: "short",
                    })
                  : "—"}
              </div>
              <div>
                End Date:{" "}
                {settings.endDate
                  ? new Date(settings.endDate as string | Date).toLocaleString(undefined, {
                      timeZoneName: "short",
                    })
                  : "—"}
              </div>
              <div>Contact Info: {settings.contactPersons}</div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
